import { defineStore } from 'pinia';
import announcementIcon from '@/assets/icons/notification-announcement.svg';
import reminderIcon from '@/assets/icons/notification-reminder.svg';
import profileActionIcon from '@/assets/icons/notification-profile-action.svg';

const STORAGE_KEY = 'notification_read_ids';

const CATEGORY_CONFIG = {
  reminder: { icon: reminderIcon },
  profile_action: { icon: profileActionIcon },
  announcement: { icon: announcementIcon }
};

function generateHashId(content) {
  let hash = 0;
  const str = String(content || '');
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return 'notif_' + Math.abs(hash).toString(36);
}

export { CATEGORY_CONFIG };

export const useNotificationStore = defineStore('notifications', {
  state: () => ({
    notifications: [],
    readIds: new Set(),
    panelOpen: false,
    panelExpanded: false
  }),

  actions: {
    setNotifications(studentAnnouncements, partnerAnnouncement) {
      const notifications = [];

      (studentAnnouncements || []).forEach((a) => {
        if (!a) return;
        const isObject = typeof a === 'object';
        const body = isObject ? a.body : a;
        if (!body) return;

        notifications.push({
          id: isObject && a.id ? String(a.id) : generateHashId(body),
          title: isObject && a.name ? a.name : null,
          body,
          category: isObject && a.category ? a.category : 'announcement',
          date: isObject && a.createdDate ? a.createdDate : null,
          source: 'learner'
        });
      });

      if (partnerAnnouncement) {
        const body =
          typeof partnerAnnouncement === 'string'
            ? partnerAnnouncement
            : partnerAnnouncement.content || partnerAnnouncement;

        if (body) {
          notifications.push({
            id: generateHashId(body),
            title: typeof partnerAnnouncement === 'object' ? partnerAnnouncement.name : null,
            body: String(body),
            category: 'announcement',
            date: null,
            source: 'partner'
          });
        }
      }

      this.notifications = notifications;
    },

    markAsRead(id) {
      this.readIds.add(id);
      this.persistReadState();
    },

    markAllAsRead() {
      this.notifications.forEach((n) => this.readIds.add(n.id));
      this.persistReadState();
    },

    openPanel() {
      this.panelOpen = true;
    },

    closePanel() {
      this.panelOpen = false;
      this.panelExpanded = false;
    },

    toggleExpanded() {
      this.panelExpanded = !this.panelExpanded;
    },

    loadReadState() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          this.readIds = new Set(JSON.parse(stored));
        }
      } catch {
        this.readIds = new Set();
      }
    },

    persistReadState() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...this.readIds]));
      } catch {
        // localStorage unavailable
      }
    }
  },

  getters: {
    allNotifications: (state) => {
      return state.notifications.map((n) => ({
        ...n,
        isRead: state.readIds.has(n.id),
        iconSrc: (CATEGORY_CONFIG[n.category] || CATEGORY_CONFIG.announcement).icon
      }));
    },

    unreadCount: (state) => {
      return state.notifications.filter((n) => !state.readIds.has(n.id)).length;
    },

    hasUnread: (state) => {
      return state.notifications.some((n) => !state.readIds.has(n.id));
    }
  }
});
